import type { IAdminAdvancedSearchAudience } from '../../types/global/adminUserAdvancedSearch.types';

export const types = {
    FETCH_EMAIL_TEMPLATES: 'FETCH_EMAIL_TEMPLATES',
    FETCH_EMAIL_TEMPLATES_SUCCESS: 'FETCH_EMAIL_TEMPLATES_SUCCESS',
    FETCH_EMAIL_TEMPLATES_FAIL: 'FETCH_EMAIL_TEMPLATES_FAIL',
    SAVE_EMAIL_TEMPLATE: 'SAVE_EMAIL_TEMPLATE',
    SAVE_EMAIL_TEMPLATE_SUCCESS: 'SAVE_EMAIL_TEMPLATE_SUCCESS',
    SAVE_EMAIL_TEMPLATE_FAIL: 'SAVE_EMAIL_TEMPLATE_FAIL',
    DELETE_EMAIL_TEMPLATE: 'DELETE_EMAIL_TEMPLATE',
    DELETE_EMAIL_TEMPLATE_SUCCESS: 'DELETE_EMAIL_TEMPLATE_SUCCESS',
    DELETE_EMAIL_TEMPLATE_FAIL: 'DELETE_EMAIL_TEMPLATE_FAIL',
    SEND_EMAIL: 'SEND_EMAIL',
    SEND_EMAIL_SUCCESS: 'SEND_EMAIL_SUCCESS',
    SEND_EMAIL_FAIL: 'SEND_EMAIL_FAIL',
    SET_CURRENT_TEMPLATE: 'SET_CURRENT_TEMPLATE',
    FETCH_PRODUCT_EMAIL_CATEGORIES: 'FETCH_PRODUCT_EMAIL_CATEGORIES',
    FETCH_PRODUCT_EMAIL_CATEGORIES_SUCCESS: 'FETCH_PRODUCT_EMAIL_CATEGORIES_SUCCESS',
    FETCH_PRODUCT_EMAIL_CATEGORIES_FAIL: 'FETCH_PRODUCT_EMAIL_CATEGORIES_FAIL',
};

export interface IEmailTemplate {
    _id: string;
    title: string;
    html: string;
    variables: string[];
    createdAt: string;
    updatedAt: string;
    created_by?: string;
}

export interface IFetchEmailTemplatesPayload {
    page?: number;
    limit?: number;
    searchTerm?: string;
}

export interface ISaveEmailTemplatePayload {
    _id?: string;
    title: string;
    html: string;
    variables: string[];
}

export interface IDeleteEmailTemplatePayload {
    id: string;
}

export interface ISendEmailPayload {
    /** GrapesJS template blast — omit for product emails. */
    templateId?: string;
    /** Product email category (contest-open, vote-reminder, …). */
    categoryId?: string;
    entityId?: string;
    audienceScope?: 'platform' | 'participants' | 'users' | 'advanced';
    userId?: string; // Optional for single user
    userIds?: string[]; // For multiple users
    sendToAll?: boolean; // Send to all users
    /**
     * Filter-driven recipients: server re-runs advanced-search filters
     * (for large “Select all” audiences — avoids shipping thousands of ids).
     */
    audience?: IAdminAdvancedSearchAudience & {
        max_recipients?: number;
    };
    subject?: string; // Email subject
    message?: string;
    image?: string;
    note?: string;
    doNotRepeat?: boolean; // Do not repeat sending to users who already received this email
    /** Full-width hero under the email curve (vs circular badge). */
    largeHeroImage?: boolean;
    /** Wrap template body in shared Thspian header/footer when sending */
    includeBase?: boolean;
    /** Resolved video assets for `data-thspian-video-slot` markers (backend merges before send). */
    videoSlots?: Record<
        string,
        {
            projectId: string;
            videoId?: string;
            thumbnailUrl: string;
            title: string;
            creatorName?: string;
            watchUrl: string;
            ctaLabel?: string;
            showTitle?: boolean;
            showCreator?: boolean;
        }
    >;
    /** Resolved images for `data-thspian-image-slot` markers (backend merges before send). */
    imageSlots?: Record<
        string,
        {
            url: string;
            alt?: string;
            href?: string;
            assetId?: string;
            title?: string;
            body?: string;
        }
    >;
    /** Resolved copy for `data-thspian-text-slot` markers (backend merges before send). */
    textSlots?: Record<string, { text: string }>;
}

export interface IProductEmailCategory {
    id: string;
    label: string;
    description: string;
    entity: 'contest' | 'campaign' | 'audition' | null;
    logType: string;
    defaultAudience: string;
    audiences: string[];
    requiresSubject: boolean;
    requiresImage: boolean;
    supportsLargeImage?: boolean;
    contestScoped: boolean;
}

export interface IEmailTemplatesResponse {
    status: string;
    totalCount: number;
    pageSize: number;
    currentPage: number;
    totalPages: number;
    results: number;
    data: {
        data: IEmailTemplate[];
    };
}
